import React, { useContext, useEffect, useState } from 'react'
import { FaCopy, FaEye, FaEyeSlash, FaKey } from 'react-icons/fa'
import { Button, TextInput } from 'flowbite-react'
import client from '../../../../api/client'
import showToast from '../../../../components/CustomToast/toastHelper'
import { UserContext } from '../../../../contexts/UserContext'

const ApiKeys = () => {
    const [keys, setKeys] = useState({
        publicKey: "",
        secretKey: "",
        encryptionKey: ""
    });

    const [visibility, setVisibility] = useState<{
        public: boolean,
        secret: boolean,
        encryption: boolean
    }>({
        public: false,
        secret: false,
        encryption: false,
    });

    const handleToggleVisibility = (keyType: 'public' | 'secret' | 'encryption') => {
        setVisibility((prev) => ({
            ...prev,
            [keyType]: !prev[keyType],
        }));
    };

    const [subsidiaryId, setSubsidiaryId] = useState<number | null>(null);

    const { userDetails } = useContext(UserContext) ?? {};

    useEffect(() => {
        if (userDetails) {
            const { activeSubsidiary } = userDetails;
            setSubsidiaryId(activeSubsidiary.id);
        }
    }, [userDetails]);

    const [webhook, setWebhook] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            await fetchWebhook();
            await getKeys();
        }
        fetchData();
    }, []);

    const fetchWebhook = async () => {
        try {
            const response = await client.get('/subsidiary/static/account/default/webhook/view');
            setWebhook(response.data.data.callbackUrl);
            const message = response?.data?.message;
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                const message = err?.response?.data?.message;
                console.error('error getting data', err);
                showToast(message, "error");
                setWebhook('');
            }
        }
    }

    const getKeys = async () => {
        try {
            const response = await client.get('/subsidiary/dashboard/subsidiary/get-keys');

            setKeys(response.data.apiKeys || { publicKey: "", secretKey: "", encryptionKey: "" });
            const message = response?.data?.message;
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                const message = err?.response?.data?.message;
                console.error('error getting data', err);
            }
        }
    }

    const generateNewKeys = async () => {
        try {
            const response = await client.post('/subsidiary/dashboard/auth/new-key');
            const message = response?.data?.message;
            showToast(message, "success");
            await getKeys();
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                const message = err?.response?.data?.message;
                console.error('error getting data', err);
            }
        }
    }

    const saveWebhook = async () => {
        const payload = {
            defaultWebhook: webhook,
            subsidiaryId: subsidiaryId
        };
        try {
            const response = await client.post('/subsidiary/static/account/default/webhook', payload);

            setWebhook(response.data.data);
            const message = response?.data?.message;
            showToast(message, "success");
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                const message = err?.response?.data?.message;
                console.error('error getting data', err);
            }
        }
    }

    const handleCopyKey = (keyType: 'public' | 'secret' | 'encryption') => {
        let keyToCopy = '';
        switch (keyType) {
            case 'public':
                keyToCopy = keys.publicKey || '';
                break;
            case 'secret':
                keyToCopy = keys.secretKey || '';
                break;
            case 'encryption':
                keyToCopy = keys.encryptionKey || '';
                break;
            default:
                break;
        }
        navigator.clipboard.writeText(keyToCopy);
        showToast(`${keyType} key has been copied`, "success");
    };

    const displayKey = (keyType: 'public' | 'secret' | 'encryption', length: number = 50) => {
        let key = '';
        switch (keyType) {
            case 'public':
                key = keys.publicKey;
                break;
            case 'secret':
                key = keys.secretKey;
                break;
            case 'encryption':
                key = keys.encryptionKey;
                break;
            default:
                break;
        }
    
        const displayLength = Math.min(length, 50);
        const visiblePart = key.substring(0, displayLength);
        const obfuscatedPart = '*'.repeat(Math.max(key.length - displayLength, 0));
    
        if (visibility[keyType] || key.length <= displayLength) {
            return key.length > displayLength ? `${visiblePart}${obfuscatedPart}` : key;
        } else {
            return `${visiblePart.replace(/./g, '*')}...`;
        }
    };
    

    return (
        <div className=''>
            <div className='grid grid-cols-1 md:grid-cols-2'>
                <div className='mx-4'>
                    <p className='text-3xl font-semibold mb-3'>API Keys</p>
                    <p>API Keys for integrations</p>
                </div>
                <div>
                    <div className=''>
                        <div className='my-3'>
                            <div className='flex items-center my-3'>
                                <div className='flex items-center gap-2'>
                                    <FaKey color='green' />
                                    <p>Public key</p>
                                    <div onClick={() => handleToggleVisibility('public')} className='cursor-pointer'>
                                        {visibility.public ? <FaEyeSlash /> : <FaEye />}
                                    </div>
                                </div>
                                <div onClick={() => handleCopyKey('public')} className='flex items-center ml-auto gap-2 cursor-pointer'>
                                    <FaCopy />
                                    <p className='text-sm'>Copy Code</p>
                                </div>
                            </div>
                            <div className='h-10 bg-green-50 rounded-lg py-6 flex items-center px-4 font-semibold lg:my-2'>
                                {displayKey('public', 35)}
                            </div>
                        </div>
                        <div className='my-3'>
                            <div className='flex items-center'>
                                <div className='flex items-center gap-2'>
                                    <FaKey color='green' />
                                    <p>Secret Key</p>
                                    <div onClick={() => handleToggleVisibility('secret')} className='cursor-pointer'>
                                        {visibility.secret ? <FaEyeSlash /> : <FaEye />}
                                    </div>
                                </div>
                                <div onClick={() => handleCopyKey('secret')} className='flex items-center ml-auto gap-2 cursor-pointer'>
                                    <FaCopy />
                                    <p className='text-sm'>Copy Code</p>
                                </div>
                            </div>
                            <div className='h-10 bg-green-50 rounded-lg py-6 flex items-center px-4 font-semibold my-2'>
                                {displayKey('secret', 35)}
                            </div>
                        </div>
                        <div className='my-3'>
                            <div className='flex items-center'>
                                <div className='flex items-center gap-2'>
                                    <FaKey color='green' />
                                    <p>Encryption Key</p>
                                    <div onClick={() => handleToggleVisibility('encryption')} className='cursor-pointer'>
                                        {visibility.encryption ? <FaEyeSlash /> : <FaEye />}
                                    </div>
                                </div>
                                <div onClick={() => handleCopyKey('encryption')} className='flex items-center ml-auto gap-2 cursor-pointer'>
                                    <FaCopy />
                                    <p className='text-sm'>Copy Code</p>
                                </div>
                            </div>
                            <div className='h-10 bg-green-50 rounded-lg py-6 flex items-center px-4 font-semibold my-2 overflow-hidden truncate'>
                                {displayKey('encryption', 35)}
                            </div>
                        </div>
                    </div>
                    <div className='flex justify-end'>
                        <Button className='bg-custom-button-green' onClick={generateNewKeys}>Generate New Keys</Button>
                    </div>
                </div>

            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 mt-28'>
                <div className='mx-4'>
                    <p className='text-3xl font-semibold mb-3'>Webhooks</p>
                    <p>Type your webhook URL here</p>
                </div>
                <div className=''>
                    <div>
                        <p className='flex items-center gap-2 my-2'><FaKey /> Webhooks</p>
                        <TextInput value={webhook} onChange={(e) => setWebhook(e.target.value)} />
                    </div>
                    <div className='flex justify-end mt-2'>
                        <Button className='bg-custom-button-green text-black' onClick={saveWebhook}>Add Hook</Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ApiKeys;
